import * as React from 'react';
import type { CalendarEvent } from '../../../MedicareProAPI/Entities/CalendarEvent';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import { DAYS } from '../../../MedicareProAPI/Utilities/Languages';
import { User } from '../../../MedicareProAPI/Entities/User/User';
import useScrollTo from '../../Effect/ScrollTo';

export interface CalendarWeekProps {
  events?: CalendarEvent[];
  dateTime?: Date;
  onEdit?: (calendarEvent: CalendarEvent) => void;
  showWeekend?: boolean;
}

export interface CalendarWeekDay {
  label: string;
  date: number;
}

export const CalendarWeek = (props: CalendarWeekProps): React.JSX.Element => {
  const [daysOfWeek, setDaysOfWeek] = React.useState<CalendarWeekDay[]>();
  const [dateTime, setDateTime] = React.useState<Date>();
  const [events, setEvents] = React.useState<CalendarEvent[][]>();
  const [allDayEvents, setAllDayEvents] = React.useState<CalendarEvent[][]>();
  const [showWeekend, setShowWeekend] = React.useState<boolean>(true);

  const hoursInDay = 24;
  const hours = Array.from(Array(hoursInDay).keys());
  const hourHeight = 70;

  const date = new Date();
  const today = date.getDate();
  const todayDay = date.getDay();
  const hourNow = date.getHours();
  const minuteNow = date.getMinutes();
  const currentTimeOffset = ((hourNow * 60 + minuteNow) / 60) * 69;

  // Handle auto scroll for week view
  const weekScrollRef = React.useRef<HTMLDivElement | null>(null);
  useScrollTo(weekScrollRef, { yCoord: 84 * 5 });

  React.useEffect(() => {
    if (props.dateTime) {
      setDateTime(props.dateTime);
      const daysList: CalendarWeekDay[] = [];
      const currentDayOfWeek = Utilities.getFirstDayOfWeek(props.dateTime);

      DAYS.forEach((daySingle, dayIndex) => {
        const singleDay: CalendarWeekDay = {
          date: currentDayOfWeek.getDate(),
          label: daySingle,
        };

        daysList.push(singleDay);

        currentDayOfWeek.setDate(currentDayOfWeek.getDate() + 1);
      });

      setDaysOfWeek(daysList);
    }
  }, [props.dateTime]);

  React.useEffect(() => {
    if (props.events) {
      const events: CalendarEvent[][] = [[], [], [], [], [], [], []];
      const allDayEvents: CalendarEvent[][] = [[], [], [], [], [], [], []];

      props.events.forEach((event) => {
        const startDate = event.getDateTimeStart();

        if (event.isAllDayEvent()) {
          allDayEvents[startDate.getDay()].push(event);
        } else {
          events[startDate.getDay()].push(event);
        }
      });

      setAllDayEvents(allDayEvents);
      setEvents(events);
    }
  }, [props.events]);

  React.useEffect(() => {
    setShowWeekend(props.showWeekend);
  }, [props.showWeekend]);

  const containerClassNames = [
    'calendar-week-container',
    showWeekend ? 'show-weekend' : 'hide-weekend',
  ];

  return (
    <div className={containerClassNames.join(' ')}>
      <div className="week-names-container">
        <div className="week-names">
          <div className="day"></div>
          {daysOfWeek &&
            daysOfWeek.map((day, dayIndex) => (
              <div className={`day text-center day-${dayIndex}`} key={`dayhead-${dayIndex}`}>
                <div
                  style={{ fontSize: '11pt' }}
                  className={`pt-2  ${today == day.date ? 'font-weight-bold text-success' : ''}`}
                >
                  {day.label} {day.date}
                </div>
                {allDayEvents && allDayEvents[dayIndex] && (
                  <div className="events">
                    {allDayEvents[dayIndex].map((eventSingle: CalendarEvent) => {
                      return (
                        <div
                          className="badge badge-sm border badge-primary border-primary text-left cp"
                          onClick={(e) => props.onEdit(eventSingle)}
                          key={`event-${eventSingle.getId()}`}
                        >
                          {eventSingle.getName()}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
      <div className="calendar-week-container-inner" ref={weekScrollRef}>
        <div className="week-events-container">
          <div className="week-events">
            <div className="day">
              {hours.map((hour) => (
                <div key={`hour-${hour}`} className="week-day p-2 small">
                  <span
                    style={{ fontSize: '11pt' }}
                    className={`${hourNow == hour ? 'text-danger font-weight-bold' : ''}`}
                  >
                    {Utilities.renderAMPM(hour)}
                  </span>
                  <span className={` ml-1 ${hourNow == hour ? 'text-danger' : ''}`}>
                    {Utilities.renderAMPMString(hour)}
                  </span>
                </div>
              ))}
            </div>
            {DAYS.map((day, dayIndex) => {
              return (
                <div key={`day-${dayIndex}`} className={`day day-${dayIndex}`}>
                  {events &&
                    events[dayIndex] &&
                    events[dayIndex].map((eventSingle: CalendarEvent) => {
                      const dateTimeStart = eventSingle.getDateTimeStart();
                      const dateTimeEnd = eventSingle.getDateTimeEnd();

                      const offset =
                        ((dateTimeStart.getHours() * 60 + dateTimeStart.getMinutes()) / 60) *
                        hourHeight;
                      const height =
                        ((dateTimeEnd.getTime() - dateTimeStart.getTime()) / 1000 / 60 / 60) *
                        hourHeight;

                      return (
                        <div
                          style={{ marginTop: offset + 'px', height: height + 'px' }}
                          className="badge badge-sm border badge-primary border-primary text-left"
                          onClick={(e) => props.onEdit(eventSingle)}
                          key={eventSingle.getId()}
                        >
                          {eventSingle.getName()}
                          {eventSingle.getInds() && eventSingle.getInds().length > 0 && (
                            <>
                              <br />
                              <em className="font-weight-normal">
                                <>with </>
                                {eventSingle.getInds().map((ind, index) => {
                                  return (
                                    ind.getIndName() +
                                    (eventSingle.getInds().length > 1 &&
                                    index + 1 !== eventSingle.getInds().length
                                      ? ', '
                                      : '')
                                  );
                                })}
                              </em>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
